import React, { useEffect, useRef } from 'react';
import { useImmer } from 'use-immer';
import { SelectBoxComponent } from '@/components';
import { cn } from '@/utils';
import CustomFormat from './CustomFormat';

const Format = ({
  isExpanded,
  headline,
  values,
  selection_label,
  selection_id,
  toggleDropdown,
  openDropdown,
  onItemClick,
  parentId,
  isFetching,
  loadingIndex,
  denyCustomFormats,
  shapes,
  papers,
  custom_format,
  setMultipleOverwrites,
  closeDropdown,
  lastChanges,
  bundle
}) => {
  const modalBody = useRef(null);
  const selectBoxRef = useRef();
  const dialogRef = useRef();

  const [componentState, updateComponentState] = useImmer({
    element: null,
    hideNotice: false
  });

  const hideNotice = () => {
    updateComponentState((draft) => {
      draft.hideNotice = true;
    });
  };

  const hideTooltips = (activeTooltip = null) => {
    if (modalBody.current) {
      modalBody.current.querySelectorAll(".size-selection-tooltip-wrapper").forEach((tooltipWrapper) => {
        const tooltip = tooltipWrapper.querySelector('.size-selection-tooltip');

        if (tooltip && tooltip !== activeTooltip && tooltip.classList.contains('show')) {
          tooltip.classList.remove('show');
          const toggler = tooltipWrapper.querySelector('.tooltip-toggler');
          toggler.ariaExpanded = false;
        }
      });
    }
  }

  const toggleTooltip = (e) => {
    const tooltip = e.target.classList.contains('tooltip-toggler') ? e.target.nextElementSibling
      : e.target.parentNode.nextElementSibling;
    const tooltipBtn = e.target.classList.contains('tooltip-toggler') ? e.target : e.target.parentNode;
    tooltipBtn.ariaExpanded = (tooltipBtn.ariaExpanded === 'true') ? 'false' : 'true';

    if (tooltip && tooltip.classList.contains('size-selection-tooltip')) {
      hideTooltips(tooltip);
      tooltip.classList.toggle("show");
    }
  }

  useEffect(() => {
    if (modalBody.current) {
      modalBody.current.focus();
    }
  }, []);

  useEffect(() => {
    document.body.style.overflow = isExpanded ? 'hidden' : '';

    if (dialogRef.current) {
      dialogRef.current.showModal();
    }
  }, [isExpanded]);

  useEffect(() => {
    if (isFetching) {
      hideTooltips();
    }
  }, [isFetching]);

  useEffect(() => {
    if (selectBoxRef.current && lastChanges?.format === true && lastChanges?.paper === true) {
      selectBoxRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [lastChanges?.format, lastChanges?.paper]);

  const getLineartPreviewSrc = ({shape, format, paper = 'default', context = 'format-selection'}) => {
    const parts = [
      shape,
      format,
      paper,
      context,
    ];
    return '/api/product-image/none/' + parts.join('/');
  };

  return (
    <>
      <SelectBoxComponent
        testid={process.env.RENDER_TESTIDS ? "formatselect" : undefined}
        ref={selectBoxRef}
        showIcon={values.length > 1 || (!denyCustomFormats && custom_format.is_enabled)}
        onClick={() => {
          if (values.length > 1 || (!denyCustomFormats && custom_format.is_enabled)) {
            openDropdown();
          }
        }}
        {...{headline, isExpanded, id: 'format'}}
        isDisabled={isFetching}
        disabledClassName="opacity-40 bg-neutral-light"
        isHighlighted={!componentState.hideNotice && lastChanges?.format === true && lastChanges?.paper === true}
        highlightedClassName="border-status-warning-dark"
        buttonDataAttributes={{'data-format': selection_id}}
        DropdownContent={
          <>
            <dialog
              ref={dialogRef}
              id="configurator-modal-format"
              aria-labelledby="configurator-modal-format-title"
              className="fixed inset-0 z-overlay bg-transparent w-full max-w-4xl p-0"
              onClick={(e) => {
                if (dialogRef.current && e.target === dialogRef.current) {
                  closeDropdown();
                } else if (!e.target.classList.contains('tooltip-toggler') && !e.target.parentNode.classList.contains('tooltip-toggler')) {
                  hideTooltips();
                }
              }}
              onKeyDown={(e) => {
                if (e.key === 'Escape' || e.key === 'Esc') {
                  e.preventDefault();
                  if (modalBody.current.querySelector('.size-selection-tooltip.show') !== null) {
                    hideTooltips();
                  } else {
                    closeDropdown();
                  }
                }
              }}
            >
              <div
                ref={modalBody}
                className={cn("relative overflow-x-hidden overflow-y-auto bg-white shadow w-full min-h-4xl max-h-screen tablet:max-h-[calc(100vh-5rem)]",
                  (values.filter(value => value.tooltip && value.tooltip.trim() !== '').length > 0) ? "pb-[25rem] laptop:pb-[18rem]" : ''
                )}
              >
                <div
                  className="sticky z-cover top-0 h-[55px] flex items-center justify-between bg-brand-1 pl-2xl pr-sm"
                >
                  <div id="configurator-modal-format-title" className="font-semibold text-base text-white">
                    {headline}
                  </div>
                  <button
                    onClick={() => closeDropdown()}
                    type="button"
                    className="bg-transparent border-none rounded-full h-xl w-xl"
                    aria-label={Drupal.t('Close')}
                  >
                    <ft-icon color="white" name="close" width={24} height={24}></ft-icon>
                  </button>
                </div>
                <div>
                  <If
                    condition={!denyCustomFormats && custom_format.is_enabled}>
                    <CustomFormat
                      {...{
                        shapes,
                        values,
                        onItemClick,
                        custom_format,
                        parentId,
                        setMultipleOverwrites,
                      }}
                    />
                  </If>
                  <div className="p-lg grid-2 gap-xl tablet:grid-3 tablet:p-2xl">
                    {values.map(({label, is_selected, id, tooltip, additional_information = []}, i) => {
                      return (
                        <div key={i} className={cn("relative flex items-center gap-2xs",
                          (tooltip && tooltip.length > 0) ? 'size-selection-tooltip-wrapper' : '')}>
                          <button
                            data-testid={process.env.RENDER_TESTIDS ? id : undefined}
                            data-format={id}
                            onClick={() => onItemClick({parentId, id, is_selected})}
                            className={cn(
                              'tile relative border border-neutral-darker flex grow h-auto min-h-[60px] max-h-7xl items-center justify-center p-3xs font-semibold text-xs cursor-pointer',
                              'tablet:text-sm',
                              'hover:bg-neutral-light',
                              is_selected || loadingIndex === `${parentId}_${id}`
                                ? 'bg-neutral-light ring-2 ring-inset ring-brand-1 border-brand-1'
                                : '',
                              loadingIndex === `${parentId}_${id}`
                                ? 'disabled'
                                : '',
                            )}
                          >
                            <Choose>
                              <When condition={loadingIndex === `${parentId}_${id}`}>
                                <ft-spinner size="small"></ft-spinner>
                              </When>
                              <Otherwise>
                                <Choose>
                                  <When condition={bundle === 'blank_label'}>
                                    <div className="flex w-full justify-between items-center gap-4xs">
                                      <div className="grow">
                                        <span>{label}</span>
                                        <If condition={additional_information.length > 0}>
                                          <ft-badge menu="" variant="3">
                                            {additional_information.join(', ')}
                                          </ft-badge>
                                        </If>
                                      </div>
                                      <div className="shrink-0">
                                        <img className="h-[70px]" width="50" height="70" src={getLineartPreviewSrc({
                                          shape: shapes.selection_id,
                                          format: id,
                                          paper: papers.selection_id //remove this one to suppress material simulation
                                        })} alt={label} loading='lazy'/>
                                      </div>
                                    </div>
                                  </When>
                                  <Otherwise>
                                    <div className="flex flex-col items-center gap-y-4xs">
                                      <span>{label}</span>
                                      <If condition={additional_information.length > 0}>
                                        <ft-badge menu="" variant="3">
                                          {additional_information.join(', ')}
                                        </ft-badge>
                                      </If>
                                    </div>
                                  </Otherwise>
                                </Choose>
                              </Otherwise>
                            </Choose>
                          </button>
                          <If condition={tooltip && tooltip.length > 0}>
                            <button
                              onClick={(e) => toggleTooltip(e)}
                              type="button"
                              className="tooltip-toggler bg-transparent border-none rounded-full h-xl w-xl"
                              aria-haspopup="dialog"
                              aria-expanded={false}
                              aria-label={Drupal.t('Show tooltip')}
                            >
                              <ft-icon color="white" name="info" width={24} height={24}></ft-icon>
                            </button>
                            <div
                              className="size-selection-tooltip absolute z-cover -bottom-[25rem] tablet:-bottom-[18.75rem] flex-wrap min-h-[5rem] max-h-[26rem] w-[calc(100vw-2rem)] tablet:max-w-[40rem] bg-white p-lg shadow border border-medium"
                              dangerouslySetInnerHTML={{__html: tooltip}}
                            ></div>
                          </If>
                        </div>
                      )
                    })}
                  </div>
                </div>
              </div>
            </dialog>
          </>
        }
      >
        <div className="flex items-center justify-start gap-sm">
          <If condition={bundle === 'blank_label'}>
            <img
              className="tablet:hidden flex-none w-auto! max-h-[2.5rem]"
              width="29"
              height="40"
              src={getLineartPreviewSrc({
              shape: shapes.selection_id,
              format: selection_id,
              paper: papers.selection_id //remove this one to suppress material simulation
            })} alt={selection_label} loading='lazy'/>
          </If>
          <span className="grow tablet:w-full text-center">{selection_label}</span>
        </div>
      </SelectBoxComponent>
      <If condition={!componentState.hideNotice && lastChanges?.format === true && lastChanges?.paper === true}>
        <div
          className="relative border border-status-warning-dark bg-status-warning-lighter py-2xs px-lg flex items-center justify-center -mt-lg mb-2xl">
          <span className="text-sm font-semibold">{Drupal.t('Size updated based on material choice')}</span>
          <div
            className="absolute right-0 cursor-pointer flex h-full items-center mr-xs text-status-warning-dark"
            onClick={() => hideNotice()}
          >
            <ft-icon name="close" width="20"></ft-icon>
          </div>
        </div>
      </If>
    </>
  );
};

export default Format;
